export type MarketAmazonResultFilters = {
    min_price?: string; max_price?: string; currency?: string; min_rating?: number; min_reviews?: number;
};
export const amazonFilterKeys = ['min_price', 'max_price', 'currency', 'min_rating', 'min_reviews'] as const;
export const isAmazonCapability = (capability: string) =>
    ['amazon.products.collect_by_url', 'amazon.products.discover_by_keyword'].includes(capability);
type Scope = { capability: string; source_system: string; scope: { kind: string; value?: string | null }; result_filters?: unknown };
export function amazonFilterScopesMatch(requested: Scope[], actual: Scope[]): boolean {
    const relevant = (scopes: Scope[]) => scopes.filter(scope => isAmazonCapability(scope.capability))
        .map(scope => ({ capability: scope.capability, source: scope.source_system, kind: scope.scope.kind, site: scope.scope.value,
            filters: normalizeAmazonResultFilters(scope.result_filters) }))
        .sort((a, b) => JSON.stringify([a.capability, a.site]).localeCompare(JSON.stringify([b.capability, b.site])));
    try { return JSON.stringify(relevant(requested)) === JSON.stringify(relevant(actual)); }
    catch { return false; }
}

/** Exact decimal text, never Number/FX conversion. Same bounds as archived product prices. */
const price = (value: unknown): string => {
    if (typeof value !== 'string' || !/^[0-9]{1,16}(?:\.[0-9]{1,12})?$/.test(value)) throw new Error('Invalid price bound');
    const [whole, fraction = ''] = value.split('.');
    const tail = fraction.replace(/0+$/, '');
    return whole.replace(/^0+(?=\d)/, '') + (tail ? '.' + tail : '');
};
const comparablePrice = (value: string): string => {
    const [whole, fraction = ''] = value.split('.');
    return whole.padStart(16, '0') + fraction.padEnd(12, '0');
};
export function normalizeAmazonResultFilters(input: unknown): MarketAmazonResultFilters {
    if (input == null) return {};
    if (typeof input !== 'object' || Array.isArray(input)) throw new Error('Invalid Amazon filters');
    const record = input as Record<string, unknown>;
    if (Object.keys(record).some(key => !amazonFilterKeys.includes(key as typeof amazonFilterKeys[number]))) throw new Error('Unsupported Amazon filter');
    const out: MarketAmazonResultFilters = {};
    for (const key of ['min_price', 'max_price'] as const) if (key in record) out[key] = price(record[key]);
    const bounded = out.min_price !== undefined || out.max_price !== undefined;
    if (bounded !== ('currency' in record) || 'currency' in record && (typeof record.currency !== 'string' || !/^[A-Z]{3}$/.test(record.currency)))
        throw new Error('Price bounds need a currency');
    if (bounded) out.currency = record.currency as string;
    if (out.min_price !== undefined && out.max_price !== undefined && comparablePrice(out.min_price) > comparablePrice(out.max_price))
        throw new Error('Reversed price bounds');
    if ('min_rating' in record) {
        if (typeof record.min_rating !== 'number' || !Number.isFinite(record.min_rating) || record.min_rating < 0 || record.min_rating > 5) throw new Error('Invalid minimum rating');
        out.min_rating = record.min_rating;
    }
    if ('min_reviews' in record) {
        if (typeof record.min_reviews !== 'number' || !Number.isSafeInteger(record.min_reviews) || record.min_reviews < 0) throw new Error('Invalid review minimum');
        out.min_reviews = record.min_reviews;
    }
    return out;
}
